import type { IconType } from 'react-icons'
import { TbWorld, TbEdit, TbMathFunction } from 'react-icons/tb'

import {
  AboutNavIcon,
  AiNavIcon,
  AppsNavIcon,
  BlogNavIcon,
  BusinessIntelligenceNavIcon,
  CalculationsNavIcon,
  CareerNavIcon,
  CaseStudiesNavIcon,
  ChangelogNavIcon,
  CommunityNavIcon,
  CompareNavIcon,
  CustomerSupportNavIcon,
  CustomersNavIcon,
  DatabricksNavIcon,
  DataInputNavIcon,
  DevelopersNavIcon,
  DataModelingNavIcon,
  DbtNavIcon,
  DocumentsNavIcon,
  EmbeddedAnalyticsNavIcon,
  EventsNavIcon,
  ExploTransitionNavIcon,
  FinancialServicesNavIcon,
  HexNavIcon,
  IntegrationsNavIcon,
  LookerNavIcon,
  MediaAdvertisingNavIcon,
  MetabaseNavIcon,
  OmnivisionNavIcon,
  PartnersNavIcon,
  PowerBINavIcon,
  RetailNavIcon,
  SaasNavIcon,
  SecurityNavIcon,
  SigmaNavIcon,
  SnowflakeNavIcon,
  SpreadsheetsNavIcon,
  SubscribeNavIcon,
  TableauNavIcon,
  ThoughtSpotNavIcon,
  StrategyNavIcon,
  UserGuidesNavIcon,
} from '../components-v2/icons/nav-icons-raster'

export type NavItem = {
  label?: string
  to?: string // now optional
  icon?: IconType
  subTitle?: string
  children?: NavItem[]
  externalLink?: boolean
  dividerBelow?: boolean
}

export type Column = {
  title?: string
  /**
   * Master–detail layout: render `items` as a list in the first inner column,
   * and the hovered item's `children` in the second inner column.
   */
  hasChildren?: boolean
  items: NavItem[]
}

type MenuItem = {
  title: string
  to?: string
  columns?: Column[]
  icon?: IconType
  subTitle?: string
  megaMenu?: boolean
  labelOnly?: boolean
  externalLink?: boolean
}

export const menuItems: MenuItem[] = [
  {
    title: 'Product',
    icon: TbWorld,
    megaMenu: true,
    columns: [
      {
        title: 'Platform',
        items: [
          {
            label: 'AI',
            to: '/ai',
            icon: AiNavIcon,
            subTitle: 'Ask questions, get answers you trust',
          },
          {
            label: 'Semantic Layer',
            to: '/semantic-layer',
            icon: DataModelingNavIcon,
            subTitle: 'Metrics, context, & joins in one platform.',
          },
          {
            label: 'Business Intelligence',
            to: '/business-intelligence',
            icon: BusinessIntelligenceNavIcon,
            subTitle: 'Insights your team trusts',
          },
          {
            label: 'Embedded Analytics',
            to: '/embedded-analytics',
            icon: EmbeddedAnalyticsNavIcon,
            subTitle: 'Customized data & AI in your product',
          },
          {
            label: 'Integrations',
            to: '/integrations',
            icon: IntegrationsNavIcon,
            subTitle: 'Connect to data sources',
          },
        ],
      },
      {
        title: 'Capabilities',
        items: [
          {
            label: 'Apps',
            to: '/apps',
            icon: AppsNavIcon,
            subTitle: 'Purpose-built apps on your data',
          },
          {
            label: 'Spreadsheets',
            to: '/spreadsheets',
            icon: SpreadsheetsNavIcon,
            subTitle: 'Complex modeling & rich formatting',
          },
          {
            label: 'Calculations',
            to: '/calculations',
            icon: CalculationsNavIcon,
            subTitle: 'Excel formulas on live data',
          },
          {
            label: 'Data Input',
            to: '/data-input',
            icon: DataInputNavIcon,
            subTitle: 'Add external data, join, & analyze',
          },
        ],
      },
    ],
  },
  {
    title: 'Solutions',
    columns: [
      {
        title: 'By Industry',
        items: [
          {
            label: 'Media & Advertising',
            to: '/industry/media-advertising',
            icon: MediaAdvertisingNavIcon,
            subTitle: 'Measure ROI with AI',
          },
          {
            label: 'Financial Services',
            to: '/industry/financial-services',
            icon: FinancialServicesNavIcon,
            subTitle: 'Compliant, consistent AI analytics',
          },
          {
            label: 'SaaS',
            to: '/industry/saas',
            icon: SaasNavIcon,
            subTitle: 'Track subscriptions, churn & more',
          },

          {
            label: 'Retail',
            to: '/industry/retail',
            icon: RetailNavIcon,
            subTitle: 'Unify sales, inventory, & customer data',
          },
        ],
      },
    ],
  },
  {
    title: 'Resources',
    icon: TbMathFunction,
    megaMenu: true,
    columns: [
      {
        title: 'Learn',
        items: [
          {
            label: 'Blog',
            to: '/blog',
            icon: BlogNavIcon,
            subTitle: 'Hear from our team',
          },
          {
            label: 'Events',
            to: '/events',
            icon: EventsNavIcon,
            subTitle: 'Webinars & where to find us',
          },
          {
            label: 'Documentation',
            subTitle: 'Setup & best practices',
            to: 'https://docs.omni.co',
            icon: DocumentsNavIcon,
            // externalLink: false,
          },
          {
            label: 'User Guides',
            subTitle: 'Step-by-step tutorials',
            to: 'https://docs.omni.co/guides',
            icon: UserGuidesNavIcon,
          },
          {
            label: 'Developers',
            to: '/developers',
            icon: DevelopersNavIcon,
            subTitle: 'Build analytics like software',
          },
          {
            label: 'Community',
            subTitle: 'Share, learn, get help',
            to: 'https://community.omni.co/',
            icon: CommunityNavIcon,
            // externalLink: true,
          },
          {
            label: 'Changelog',
            to: 'https://docs.omni.co/changelog',
            icon: ChangelogNavIcon,
            subTitle: 'Latest features & updates',
            // externalLink: true,
          },
          {
            label: 'Security',
            to: '/security',
            icon: SecurityNavIcon,
            subTitle: 'SOC2, HIPAA, & more',
          },
        ],
      },
      {
        title: 'Discover',
        hasChildren: true,
        items: [
          {
            label: 'Customers',
            icon: CustomersNavIcon,
            children: [
              {
                label: 'Case Studies',
                to: '/customer-case-studies',
                icon: CaseStudiesNavIcon,
                subTitle: 'How customers use Omni',
              },
              {
                label: 'Customer Support',
                to: '/customer-support',
                icon: CustomerSupportNavIcon,
                subTitle: 'White-glove help for every customer',
              },
            ],
          },
          {
            label: 'Partners',
            icon: PartnersNavIcon,
            children: [
              {
                label: 'Partner Program',
                to: '/partners',
                icon: PartnersNavIcon,
              },
              {
                label: 'Snowflake',
                to: '/snowflake',
                icon: SnowflakeNavIcon,
              },
              {
                label: 'Databricks',
                to: '/databricks',
                icon: DatabricksNavIcon,
              },
              {
                label: 'dbt',
                to: '/dbt',
                icon: DbtNavIcon,
              },
            ],
          },
          {
            label: 'Compare',
            icon: CompareNavIcon,
            subTitle: undefined,
            children: [
              {
                label: 'Omni vs. Tableau',
                to: '/omni-vs-tableau',
                icon: TableauNavIcon,
              },
              {
                label: 'Omni vs. PowerBI',
                to: '/omni-vs-power-bi',
                icon: PowerBINavIcon,
              },
              {
                label: 'Omni vs. Looker',
                to: '/omni-vs-looker',
                icon: LookerNavIcon,
              },
              {
                label: 'Omni vs. Hex',
                to: '/omni-vs-hex',
                icon: HexNavIcon,
              },
              {
                label: 'Omni vs. Sigma',
                to: '/omni-vs-sigma',
                icon: SigmaNavIcon,
              },
              {
                label: 'Omni vs. ThoughtSpot',
                to: '/omni-vs-thoughtspot',
                icon: ThoughtSpotNavIcon,
              },
              {
                label: 'Omni vs. Metabase',
                to: '/omni-vs-metabase',
                icon: MetabaseNavIcon,
              },
              {
                label: 'Omni vs. Strategy',
                to: '/omni-vs-strategy',
                icon: StrategyNavIcon,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Engineering Demos',
    to: 'https://docs.omni.co/demos',
    externalLink: true,
  },
  {
    title: 'Company',
    icon: TbEdit,
    columns: [
      {
        items: [
          {
            label: 'About',
            to: '/about',
            icon: AboutNavIcon,
            subTitle: "Why we're building Omni",
          },
          {
            label: 'Careers',
            to: 'https://jobs.ashbyhq.com/omni',
            icon: CareerNavIcon,
            subTitle: 'Build Omni with us',
          },
          {
            label: 'Subscribe',
            to: '/subscribe',
            icon: SubscribeNavIcon,
            subTitle: 'Stay up to date with Omni',
          },
          {
            label: 'Omnivision',
            to: '/omnivision',
            icon: OmnivisionNavIcon,
            subTitle: 'Omni’s global conference series',
          },
          {
            label: 'Explo Transition',
            to: '/explo',
            icon: ExploTransitionNavIcon,
            subTitle: 'Welcome, Explo customers!',
          },
        ],
      },
    ],
  },
]
